import {type HTMLAttributes, type RefCallback, useCallback, useEffect, useEffectEvent, useRef, useState} from 'react'

import {useBoardElementRef} from '../provider/contexts'
import {isReaderMoving, subscribeToReaderMovement} from './reader-movement'

/**
 * How far short of reaching an element it still counts as reached.
 *
 * Zero would ask for the next page only once the reader has arrived at the end of what
 * is loaded, so every page would end in a wait. This asks while there is still something
 * left to scroll through.
 *
 * Both properties, because they buy the lead in different places and neither covers the
 * other. `rootMargin` expands the viewport, which is what a board sitting at the bottom
 * of a long page needs. `scrollMargin` expands the clip of every scroll container in
 * between, which is the one that matters most here: the board and each of its cells clip
 * their own sentinels, and `rootMargin` does nothing about either. Browsers without
 * `scrollMargin` ignore it and page at the moment the sentinel arrives, which is the
 * behaviour this is improving on rather than depending on.
 *
 * One flat distance for three containers of very different depths fits none of them well,
 * and https://github.com/github/views/issues/174 is where scaling it to its container is
 * tracked. It is no longer what decides whether a direction can ask again, which is what
 * made it harmful rather than merely imprecise: see below.
 */
const REACHED_MARGIN = '200px'

/**
 * Calls `onReached` when the reader reaches the element the returned ref is attached to,
 * and again when they keep going once they are there.
 *
 * This is the whole of the board's paging mechanism, and it is the same one in all three
 * directions. There is no control to press: a direction that has more mounts one of
 * these at the end of what is loaded, and reaching it is what asks for the next page.
 *
 * The observer is created against the viewport rather than against one of the board's
 * scroll containers, and does not need to know which one it is inside. An element
 * scrolled out of a cell is clipped by that cell whatever the observer's root is, so a
 * sentinel at the bottom of a cell nobody has scrolled is correctly reported as out of
 * view.
 *
 * Nothing is observed while `enabled` is false, so a direction with nothing left to load
 * costs no observer even if its element stays mounted.
 *
 * ## One question per movement, asked where the reader stopped
 *
 * The rule is that **only the reader may raise the question, and only once they have
 * stopped**. A direction asks when it is at the end of what is loaded and the reader
 * comes to rest there, and it asks nothing else, ever.
 *
 * Everything else follows from that. A movement is reported once, in
 * `reader-movement.ts`, when it ends. So a fling that crosses forty screenfuls costs one
 * question rather than the twenty-five a 40-notch fling was measured costing.
 *
 * Only a movement of this board counts, which is what the scope below is for. The input
 * is watched on the window, so without it a click in the page header, a pause while
 * typing in a filter box, or a reader scrolling a second board on the same page would be
 * heard by every sentinel in view and answered with a page apiece.
 *
 * Rest rather than the moment the end comes into view, which was tried and is worse than
 * it sounds. A page asked for mid-movement lands while that movement is still being
 * delivered, and the momentum left in it is spent scrolling through the very cards it
 * brought: measured on a real board, a fling that asked at one card left the reader four
 * cards beyond it, past everything it had fetched. Waiting costs a reader who has stopped
 * a moment before their page is asked for. Not waiting costs them the page.
 *
 * A movement that never rests is reported anyway, on a timer. Not everything that scrolls
 * is a reader, and waiting for a smooth scroll elsewhere on the page or the board's own
 * auto-scroll to stop would mean waiting forever.
 *
 * ## Why a page arriving must not ask for the next one
 *
 * This is the whole reason the flag below is set unconditionally rather than only while
 * the reader is moving, and it is worth being blunt about because the obvious version of
 * this design does not work.
 *
 * A cell draws one skeleton per incoming card while a page is in flight, so a page lands
 * in room that was already reserved and the layout moves under the sentinel as it does.
 * Every one of those movements crosses the sentinel in or out of view. Treat a crossing
 * as a reach and the board pages itself: each delivered page fires the crossings that ask
 * for the next, with nobody touching anything. Measured on a real board, one fling
 * produced one request while the reader moved and **four more after they stopped**.
 *
 * So an arrival in view asks only if the reader has moved since this direction last
 * asked. A crossing caused by a page landing finds the flag already set and says nothing,
 * and the same flag is what keeps a caller that answers with nothing from being looped:
 * nothing but the reader clears it.
 *
 * The crossing still matters for the first question. A sentinel that comes into view
 * without the reader having moved this direction into it -- on mount, or as a column
 * scrolls into the board's own window -- has genuinely been reached for the first time.
 *
 * ## Asking once for one question
 *
 * That leaves the question that is a duplicate rather than a second page: raised while
 * the page the last one asked for has not yet been answered. The board fetches nothing
 * and holds no cursor, so what it goes on is the caller's own statement that a page for
 * this direction is in flight. While `isLoading` stands, a question is the same one
 * already outstanding and is not asked twice.
 *
 * Such a question is held rather than dropped, and handed over once that page is over. A
 * sentinel sitting in view has no second arrival to give, and a board too short to scroll
 * can produce no further crossing at all, so dropping the one it had would strand the
 * direction on a page that failed. Exactly one is held, and only when a question really
 * did land while a page was out, because a direction re-armed on every settle would ask
 * again on its own and put a caller whose request keeps failing into a loop.
 *
 * The guard is deliberately not a record of having fetched. A flag set when a page is
 * asked for and cleared when one arrives latches on the page that never arrives, and the
 * direction that most needs to ask again is the one that can no longer ask at all.
 * Nothing here distinguishes a page that landed from a page that failed, because the two
 * mean the same thing to a sentinel: the question it asked is over, and the next one is
 * new. That is what makes a failed page retryable rather than terminal.
 *
 * A caller that never states `isLoading` keeps the part of the guard that spans a render
 * and gives up the part that spans a fetch. That is the contract working as intended
 * rather than a gap in it: the board derives no fact it was not told, and `isLoading` is
 * the only thing either side knows about a request being out.
 */
export function usePagingSentinel({onReached, enabled, isLoading}: PagingSentinelOptions): RefCallback<HTMLElement> {
  // The element is held as state rather than in a ref, because attaching it has to
  // re-run the effect below. A ref would leave the observer waiting on a mount React
  // never tells it about.
  const [element, setElement] = useState<HTMLElement | null>(null)

  // The board this sentinel belongs to, which is the box a movement has to have happened
  // inside to be about it. Input is watched on the window, because a reader who has run
  // out of cell to scroll moves nothing at all and the events are the only sign of them;
  // without a box to compare against, every wheel, key and click anywhere on the page is
  // read as this reader arriving here.
  const boardElementRef = useBoardElementRef()

  // Where the sentinel was last seen, so an arrival can be told from a report about a
  // sentinel that never left, and so a gesture can tell whether the reader it belongs to
  // is standing at the end of this direction. It is held here rather than inside the
  // observer's closure so that it outlives the observer: observing an element that is
  // already in view delivers a fresh intersecting record, and an observer rebuilt for any
  // reason would otherwise read that as an arrival and ask for the page again.
  const wasInViewRef = useRef(false)

  // A question that landed while a page was already in flight. It is held rather than
  // dropped, because a sentinel sitting in view has no second arrival to give: a board
  // too short to scroll can produce no further crossing, and dropping the one it had
  // would strand the direction on a page that failed.
  const pendingArrivalRef = useRef(false)

  // This direction has asked, and nothing the reader did has happened since. Cleared only
  // by the reader moving, which is what keeps a page arriving from asking for the next
  // one: a delivery changes the cell's layout, and every crossing that causes would
  // otherwise be read as the reader arriving somewhere.
  const hasAskedRef = useRef(false)

  // `onReached` closes over the address it pages and the caller's callback, so it is a
  // new function on most renders, and `isLoading` changes as the caller answers. Reading
  // both through an effect event keeps them out of the effect's dependencies, so the
  // observer survives a re-render rather than being disconnected and rebuilt. Rebuilding
  // it would re-observe an element already in view, which is the arrival this hook has
  // to tell apart from a real one.
  const ask = useEffectEvent(() => {
    if (hasAskedRef.current) return

    // Unconditionally, and before the question is even placed. A question that is merely
    // held counts as asked, because it is the same question. Setting this only while a
    // movement was running is what let a board nobody was touching page itself: the flag
    // stayed clear, so each delivered page asked for the next.
    hasAskedRef.current = true

    // A reader who is still going is not asking for anything yet, whatever crossed. The
    // page would land while their movement was still being delivered and be spent
    // scrolling through the very cards it brought. The flag above stands, so this is the
    // one question the movement gets; coming to rest clears it and asks it properly, and
    // coming to rest somewhere else drops it, which is the right answer for a reader who
    // has gone somewhere else.
    if (isReaderMoving(boardElementRef)) return

    if (isLoading) {
      pendingArrivalRef.current = true
      return
    }

    pendingArrivalRef.current = false
    onReached()
  })

  // Hands over the one question that was held, once the page it was held behind is over.
  // Exactly one, and only if a question really did land while that page was out: a
  // direction re-armed on every settle would ask again on its own, and a caller whose
  // request keeps failing would be put in a loop by the guard meant to protect it.
  const reportHeldArrival = useEffectEvent(() => {
    if (isLoading || !pendingArrivalRef.current) return

    pendingArrivalRef.current = false
    onReached()
  })

  const handleReaderStopped = useEffectEvent(() => {
    // The reader has been somewhere and stopped, so whatever this direction asked for
    // last is spent and it may ask again. One movement, however far it travelled, buys
    // one question.
    hasAskedRef.current = false

    // Having stopped at the end of what is loaded is the reader waiting on the page the
    // ghost told them about. Nothing has to have crossed to get here: a page that landed
    // in the room its own skeleton reserved moves nothing at all, so this is the only
    // signal that they are still standing there.
    if (wasInViewRef.current) ask()
  })

  const ref = useCallback<RefCallback<HTMLElement>>(node => {
    setElement(node)
  }, [])

  useEffect(() => {
    if (element === null || !enabled) {
      // Nothing is watched, so nothing is in view as far as this hook is concerned. A
      // direction that stated it had nothing more and then found it does starts from out
      // of view, so its sentinel counts as arriving even though it never moved, and a
      // question held for a direction that has since said it is complete is dropped.
      wasInViewRef.current = false
      pendingArrivalRef.current = false
      hasAskedRef.current = false

      return
    }

    const observer = new IntersectionObserver(
      entries => {
        // Records are an ordered queue of crossings, not a snapshot, so they are walked
        // in order. Reading only the last one would lose the arrival in a queue that
        // ends where it started, and a lost arrival is a direction that quietly stops
        // paging. At most one page is asked for per delivery, because a sentinel that
        // crossed in and out several times between two frames has still only arrived
        // somewhere once as far as the reader is concerned.
        let hasArrived = false

        for (const entry of entries) {
          if (entry.isIntersecting === wasInViewRef.current) continue

          wasInViewRef.current = entry.isIntersecting
          if (entry.isIntersecting) hasArrived = true
        }

        if (hasArrived) ask()

        // Where the delivery ended is what decides whether a held question is still worth
        // handing over, and it is read after the arrival above rather than inside the
        // loop, because an arrival and a departure in one delivery ends out of view no
        // matter which order the guard sees them in. The reader who left before the page
        // they asked for settled is no longer at the end, so there is nothing to hand
        // them when it does.
        if (!wasInViewRef.current) pendingArrivalRef.current = false
      },
      {rootMargin: REACHED_MARGIN, scrollMargin: REACHED_MARGIN},
    )
    observer.observe(element)

    // The other half of what a reach is. Subscribed alongside the observer rather than on
    // its own, because a gesture is only worth hearing about where there is a sentinel in
    // view to hear it, and both are answers about the same element. Scoped to the board,
    // so what it hears about is a reader moving this board rather than the page around it.
    const unsubscribe = subscribeToReaderMovement(handleReaderStopped, boardElementRef)

    return () => {
      observer.disconnect()
      unsubscribe()
    }
  }, [boardElementRef, element, enabled])

  // Declared after the observer's effect so that a direction which stated it is complete
  // has already dropped whatever it was holding by the time this runs.
  useEffect(() => {
    reportHeldArrival()
  }, [isLoading])

  return ref
}

/** What a direction states about itself so its sentinel can ask for one page at a time. */
export type PagingSentinelOptions = {
  /** Reports that the end of what is loaded has been reached. */
  onReached: () => void
  /**
   * This direction has a further page and somebody is listening for it. Nothing is
   * observed while it is false, so a direction with nothing left to load costs no
   * observer even if its element stays mounted.
   */
  enabled: boolean
  /**
   * A page for **this** direction is in flight, as the caller states it. A question
   * raised while it stands is the one already outstanding rather than a new one.
   *
   * A caller that never states it still gets a sentinel that asks once per gesture; it
   * gives up only the part of the guard that spans a fetch.
   */
  isLoading: boolean
}

/**
 * Spread onto the element whose arrival in view asks for the next page, which is also
 * where the board's focus lands for that direction.
 *
 * More than a ref, because the two are the same box: the thing a reader scrolls to is the
 * thing that says a page is coming, so it carries the wording, and it carries the board's
 * one tab stop while the position is on it. See `usePagingStop`.
 */
export type PagingSentinelProps = HTMLAttributes<HTMLElement> & {
  ref: RefCallback<HTMLElement>
}
